import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./styles.scss";
import useSasha from "./useSasha";
import { FaCopy, FaMicrophone, FaPaperPlane } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import Actions from "../../../redux/actions";
import Markdown from "markdown-to-jsx";
import { CopyToClipboard } from "react-copy-to-clipboard";
import useRecog from "./useRecog";
import BouncingCircles from "../../Loaders/BouncingCircles";
import Ripples from "../../Loaders/Ripples";
import BarsScale from "../../Loaders/BarsScale";
import { Link, useHistory } from "react-router-dom";
import { getDaysBetweenDates } from "../../../helper/helper";
import EpkBanner from "../../../components/Onboarding/NewOnboarding/images/epk-banner.jpg";
import LandscapePromo from "../../../components/Onboarding/NewOnboarding/videos/landscape-promo-hm.mp4";
import { Modal, ModalBody } from "react-bootstrap";

const greetings = [
  `I hope you're doing well.`,
  `Glad to have you here!`,
  `I hope you're well.`,
  `Thank you for being here.`,
  `It's great to see you.`,
];

/**
 * @function AiManager
 *
 * @description
 * A component that wraps the HV chatbot. It uses the useSasha hook to get the
 * response from the chatbot and the useRecog hook to get the transcript from the
 * speech recognition.
 *
 * @param {object} current_user - The current user object.
 * @param {object} chats - The current chats object.
 * @param {function} dispatch - The dispatch function from the Redux store.
 *
 * @returns {JSX.Element} A JSX element representing the chatbot component.
 */
export default function AiManager() {
  const current_user = useSelector((a) => a.current_user);
  const { chats } = useSelector((a) => a.chats || []);
  const dispatch = useDispatch();

  const { isListening, startListening, stopListening, transcript } = useRecog();
  const [lastMsg, setLastMsg] = useState("");
  const [epkModal, setEpkModal] = useState(false);
  const chatsDom = useRef(null);
  const inputDom = useRef(null);
  const {
    data: sasha,
    isLoading: isLoadingSasha,
    nextQuestion,
    keywords,
  } = useSasha({
    prompt: lastMsg,
  });

  const history = useHistory();

  useEffect(() => {
    if (!nextQuestion) {
      return;
    }
    if (chats.length <= 1) {
      dispatch(Actions.addChats({ msg: nextQuestion || "How can I help you today?", fromMe: false }));
    }
  }, [nextQuestion, chats]);

  useEffect(() => {
    if (chats.length <= 0) {
      let msg = `Hi ${current_user?.username}, I'm Sasha, your AI Music Manager. I hope you’re well.`;
      dispatch(
        Actions.addChats({
          msg,
          fromMe: false,
        })
      );
    }
  }, [chats, current_user]);

  const enabled = useMemo(() => !!current_user, [current_user]);

  useEffect(() => {
    if (!current_user) {
      window.location.href = "/login";
    }
  }, [current_user]);

  const scrollToLast = () => {
    setTimeout(() => {
      if (chatsDom?.current) {
        chatsDom.current.scrollTo({ top: chatsDom.current.scrollHeight + 1000, behavior: "smooth" });
      }
    }, 1);
  };

  useEffect(() => {
    if (sasha) {
      let msg = sasha;
      const matches = msg.match(/ #/g);
      if (matches && matches.length > 1) {
        msg = msg.replace(/#/g, "\\#");
      }
      dispatch(Actions.addChats({ msg, fromMe: false }));
      setLastMsg("");
    }
    scrollToLast();
  }, [sasha]);

  const onSendMsg = (e) => {
    if (e) {
      e.preventDefault();
    }
    stopListening();

    const msg = inputDom.current.value;
    if (!msg || !msg.trim()) {
      return;
    }

    dispatch(Actions.addChats({ msg, fromMe: true }));
    setLastMsg(msg);
    inputDom.current.value = "";

    scrollToLast();
  };

  useEffect(() => {
    if (isListening) {
      inputDom.current.value = transcript;
    }
  }, [transcript, isListening]);

  const handleClickRecog = (e) => {
    e.preventDefault();
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const remainingDays = useMemo(() => {
    const daysPassed = getDaysBetweenDates(
      new Date(Math.max(new Date(current_user.created_at).getTime(), new Date(2024, 9, 1).getTime())),
      new Date()
    );
    const daysRemaining = Math.max(0, Math.floor(30 - daysPassed));
    if (!current_user?.is_subscribed_to_primo && daysRemaining <= 0) {
      history.push("/join");
    }
    return daysRemaining;
  }, [current_user]);

  return (
    <>
      {enabled && (
        <>
          {}
          {!current_user?.is_subscribed_to_primo && (
            <div className="chat-banner">
              <div>
                <strong>
                  {remainingDays > 0 ? (
                    <>
                      ONLY {remainingDays} DAY{remainingDays >= 10 ? "S" : ""} OF YOUR FREE PRIMO TRIAL LEFT
                    </>
                  ) : (
                    <>YOUR FREE TRIAL ENDS TODAY!</>
                  )}
                </strong>
              </div>
              <div>
                Includes FULL 24x7 access to <strong>SASHA!</strong>
              </div>
              <div>
                <Link to={"/join"} style={{ color: "unset", textDecoration: "underline" }}>
                  Subscribe now
                </Link>{" "}
                to unlock all features for just $40/month, or $400/year (save 15% off our monthly pricing with an annual plan)
              </div>
            </div>
          )}
          <div className="chat-container">
            <div className="chat-messages" ref={chatsDom}>
              {chats.map(({ msg, fromMe }, id) => (
                <div key={id} className={`chat-row ${fromMe ? "me" : "other"}`}>
                  <div className="chat-item">
                    {msg === "Have you tried UNIFI Music's EPK (electronic press kit) feature yet?" ? (
                      <>
                        Have you tried{" "}
                        <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => setEpkModal(true)}>
                          UNIFI Music's EPK (electronic press kit) feature
                        </span>{" "}
                        yet?
                      </>
                    ) : (
                      <Markdown>{msg}</Markdown>
                    )}
                  </div>
                  {!fromMe && id > 1 && msg.toLowerCase().indexOf("thanks for answer") < 0 && (
                    <CopyToClipboard text={(msg || "").replaceAll("\\#", "#")}>
                      <div style={{ marginLeft: 10 }} className="btn-icon copy">
                        <FaCopy />
                      </div>
                    </CopyToClipboard>
                  )}
                  <div style={{ flex: 1 }}></div>
                </div>
              ))}
              {isLoadingSasha && (
                <div className="chat-row other">
                  <div className="chat-item">
                    <BouncingCircles color="#888" style={{ width: 50 }} />
                  </div>
                  <div style={{ flex: 1 }}></div>
                </div>
              )}
            </div>
            {keywords && keywords.length > 0 && (
              <div>
                <div style={{ borderTop: "1px solid #0007", padding: 8, fontWeight: "bold" }}>Things You Can Ask Me</div>
                <div style={{ marginBottom: 10 }}>
                  {keywords.map((k, i) => (
                    <button
                      key={i}
                      className="btn-icon"
                      style={{ fontSize: 16, float: "left", margin: "5px", textAlign: "left" }}
                      disabled={isLoadingSasha}
                      onClick={() => {
                        inputDom.current.value = k;
                        onSendMsg(null);
                      }}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <form className="chat-input" onSubmit={onSendMsg}>
              <input ref={inputDom} name="msg" disabled={isLoadingSasha} />
              <button className="btn-icon mr-2" style={{ padding: 0 }} type="button" onClick={handleClickRecog} disabled={isLoadingSasha}>
                {isListening ? <BarsScale color="#000a" style={{ width: 36 }} /> : <FaMicrophone />}
              </button>
              <button className="btn-icon" disabled={isLoadingSasha}>
                <FaPaperPlane />
              </button>
            </form>
          </div>
        </>
      )}
      <Modal show={epkModal} onHide={() => setEpkModal(false)} size="xl">
        <ModalBody>
          <video style={{ width: "100%" }} poster={EpkBanner} muted autoPlay playsInline="" loop={true} preload="none">
            <source src={LandscapePromo} type="video/mp4" />
          </video>
        </ModalBody>
      </Modal>
    </>
  );
}
